// customer-website/src/pages/SubscriptionSuccessPage.tsx
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [canClose, setCanClose] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    console.log('[SubscriptionSuccess] Page loaded with session:', sessionId);

    // ✅ MOBILE BROWSER: Check if returning from mobile redirect (same-page)
    const checkoutPending = sessionStorage.getItem('checkout_pending');
    const returnUrl = sessionStorage.getItem('checkout_return_url');

    if (checkoutPending === 'true' && returnUrl) {
      console.log('[SubscriptionSuccess] Mobile browser redirect detected - returning to:', returnUrl);

      // Clear checkout state
      sessionStorage.removeItem('checkout_pending');
      sessionStorage.removeItem('checkout_return_url');

      // Store success flag for useMobileCheckoutReturn hook
      sessionStorage.setItem('checkout_completed', 'success');
      sessionStorage.setItem('checkout_session_id', sessionId || '');

      // Redirect back to original page
      window.location.href = returnUrl;
      return;
    }

    // ✅ WEB: Check if opened from web app (has window.opener)
    if (window.opener && !window.opener.closed) {
      try {
        console.log('[SubscriptionSuccess] Sending success message to parent window');

        // Send message to parent - same domain (www.wlkthru.co -> www.wlkthru.co)
        window.opener.postMessage(
          { type: 'STRIPE_CHECKOUT_SUCCESS', sessionId },
          window.location.origin
        );

        // Try multiple times to close the tab (browser sometimes needs delay)
        console.log('[SubscriptionSuccess] Attempting to close tab...');

        // Try immediately
        window.close();

        // Try again after 100ms
        setTimeout(() => window.close(), 100);

        // Try again after 300ms
        setTimeout(() => window.close(), 300);

        // If we're still here after 500ms, window.close() likely failed
        setTimeout(() => {
          if (!window.closed) {
            setCanClose(false);
            console.log('[SubscriptionSuccess] Auto-close failed, showing manual actions');

            // Also try to navigate parent back to billing page
            if (window.opener && !window.opener.closed) {
              try {
                window.opener.location.href = `${window.location.origin}/profile?tab=billing`;
              } catch (e) {
                console.log('[SubscriptionSuccess] Could not navigate parent:', e);
              }
            }
          }
        }, 500);
      } catch (error) {
        console.error('[SubscriptionSuccess] Failed to send message to parent:', error);
        setCanClose(false);
      }
    }
    // ✅ MOBILE: Check if running inside React Native WebView
    else if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
      try {
        console.log('[SubscriptionSuccess] Sending message to React Native WebView');
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'payment_success' })
        );

        // Mobile app will handle closing/navigation
        setTimeout(() => {
          window.location.href = 'wlkthrumobile://subscription-success';
        }, 500);
      } catch (error) {
        console.log('[SubscriptionSuccess] Not running in React Native WebView:', error);
      }
    }
    // ✅ FALLBACK: Direct navigation (no parent window)
    else {
      console.log('[SubscriptionSuccess] No parent window - user navigated directly');
      setCanClose(false);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        console.log('[SubscriptionSuccess] Redirecting to dashboard');
        navigate('/dashboard');
      }, 2000);
    }
  }, [sessionId, navigate]);

  // Minimal UI - shows briefly before auto-close
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-6 animate-scale-in">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              {canClose ? (
                <Loader2 className="w-14 h-14 text-white animate-spin" />
              ) : (
                <CheckCircle className="w-14 h-14 text-white" strokeWidth={2.5} />
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {canClose ? 'Processing...' : 'Payment Successful!'}
          </h1>
          <p className="text-gray-600">
            {canClose
              ? 'Completing your subscription...'
              : 'Welcome to wlkthru Pro!'
            }
          </p>
        </div>

        {/* Manual actions if auto-close failed */}
        {!canClose && (
          <div className="pt-4 space-y-3">
            {window.opener ? (
              <>
                <p className="text-sm text-gray-500">
                  This window should have closed automatically
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (window.opener && !window.opener.closed) {
                        window.opener.focus();
                      }
                      window.close();
                    }}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    Return to App
                  </Button>
                  <Button
                    onClick={() => window.close()}
                    variant="outline"
                    className="flex-1"
                  >
                    Close This Tab
                  </Button>
                </div>
              </>
            ) : (
              <Button
                onClick={() => navigate('/profile?tab=billing')}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                Go to Billing
              </Button>
            )}
          </div>
        )}

        {/* Redirect message if no parent window */}
        {!canClose && !window.opener && (
          <p className="text-sm text-gray-500">
            Redirecting to dashboard...
          </p>
        )}
      </div>
    </div>
  );
}
